import * as React from "react";
import { Label } from "@radix-ui/react-label";
import type { IconName } from "../../icons";
import { icons } from "../../icons";
import styles from "./TextInput.css";

interface TextInputProps {
  name: string;
  value: string;
  /** Omit it where the label already says what belongs in the field. */
  placeholder?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  leadingIcon?: IconName;
  error?: boolean;
  maxLength?: number;
  type?: "text" | "password";
  /**
   * The on-screen keyboard to offer. Deliberately separate from `type`: a
   * numeric field wants the keypad but not `type="number"`, whose browser
   * behaviour (spinners, a value that reads back as `""` for anything it deems
   * malformed) fights validation rules written against the text.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /** For an input without a visible `label`, e.g. one that only has a placeholder. */
  "aria-label"?: string;
  /**
   * Announces the input as required without `required`'s native constraint,
   * which would let the browser's own validation bubble preempt our messages.
   */
  "aria-required"?: boolean;
  /** Id of the element describing this input — normally its error message. */
  "aria-describedby"?: string;
}

const TextInput = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  required,
  disabled,
  leadingIcon,
  error,
  maxLength,
  type = "text",
  inputMode,
  "aria-label": ariaLabel,
  "aria-required": ariaRequired,
  "aria-describedby": ariaDescribedBy,
}: TextInputProps) => {
  const LeadingIcon = leadingIcon ? icons[leadingIcon] : null;

  return (
    <div className={styles.field}>
      {label && (
        <Label htmlFor={name}>
          {label} {required && "*"}
        </Label>
      )}
      <div className={styles.control({ error })}>
        {LeadingIcon && <LeadingIcon size={20} />}
        <input
          className={styles.input}
          name={name}
          id={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={error}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
          maxLength={maxLength}
          type={type}
          inputMode={inputMode}
        />
      </div>
    </div>
  );
};

export default TextInput;
